import { useMemo } from "react";
import { useParams } from "react-router";
import _ from "lodash";

import { colors } from "theme/palette";

import Skeleton from "components/Skeleton";

import { useDealChecklistPermission } from "entities/Deal/sdk";
import { EFieldStatus } from "entities/Field/constants";
import FieldStatusIcon from "entities/Package/components/FieldStatusIcon";
import {
  useCompanyPackageStatusCounts,
  useCompanyPackageWithTabsInformation,
} from "entities/Package/sdk";
import { useTaskChannels } from "entities/Task/sdk";

export interface IProgressItem {
  label: EFieldStatus;
  percentage: number;
  color: string;
  icon: JSX.Element;
}

export const useChecklistProgress = ({
  dealId: externalDealId,
  packageId: externalPackageId,
}: {
  dealId?: number;
  packageId?: number;
} = {}) => {
  const params = useParams<{
    dealId: string;
    packageId: string;
  }>();

  let dealId = Number(params.dealId);
  let packageId = Number(params.packageId);

  if (!_.isNil(externalDealId)) {
    dealId = externalDealId;
  }

  if (!_.isNil(externalPackageId)) {
    packageId = externalPackageId;
  }

  const { data: userPermission } = useDealChecklistPermission({
    dealId,
    packageId,
  });
  const hasWriteAccess = useMemo(
    () => !_.isNil(userPermission) && userPermission.has_write_access,
    [userPermission]
  );

  const { data: statusCounts } = useCompanyPackageStatusCounts({
    dealId,
    packageId,
    enabled: hasWriteAccess,
  });

  const progressData = useMemo(() => {
    if (
      _.isNil(userPermission) ||
      !hasWriteAccess ||
      _.isNil(statusCounts) ||
      statusCounts?.total_count === 0
    ) {
      return [];
    }

    if (userPermission.is_checklist_owner) {
      return [
        {
          label: EFieldStatus.SATISFIED,
          percentage:
            (statusCounts.satisfied_count / statusCounts.total_count) * 100,
          color: colors.blue120,
          icon: <FieldStatusIcon status={EFieldStatus.SATISFIED} />,
        },
        {
          label: EFieldStatus.IN_REVIEW,
          percentage:
            (statusCounts.in_review_count / statusCounts.total_count) * 100,
          color: colors.blue80,
          icon: <FieldStatusIcon status={EFieldStatus.IN_REVIEW} />,
        },
        {
          label: EFieldStatus.DONE,
          percentage:
            (statusCounts.done_count / statusCounts.total_count) * 100,
          color: colors.blue100,
          icon: <FieldStatusIcon status={EFieldStatus.DONE} />,
        },
        {
          label: EFieldStatus.NOT_DONE,
          percentage:
            (statusCounts.not_done_count / statusCounts.total_count) * 100,
          color: colors.blue60,
          icon: <FieldStatusIcon status={EFieldStatus.NOT_DONE} />,
        },
      ];
    }

    return [
      {
        label: EFieldStatus.DONE,
        percentage:
          ((statusCounts.done_count +
            statusCounts.in_review_count +
            statusCounts.satisfied_count) /
            statusCounts.total_count) *
          100,
        color: colors.blue100,
        icon: <FieldStatusIcon status={EFieldStatus.DONE} />,
      },
      {
        label: EFieldStatus.NOT_DONE,
        percentage:
          (statusCounts.not_done_count / statusCounts.total_count) * 100,
        color: colors.blue60,
        icon: <FieldStatusIcon status={EFieldStatus.NOT_DONE} />,
      },
    ];
  }, [statusCounts, hasWriteAccess, userPermission]);

  return progressData;
};

export const useNotificationCountForChecklist = () => {
  const params = useParams<{
    dealId: string;
    packageId: string;
  }>();

  const dealId = Number(params.dealId);
  const packageId = Number(params.packageId);

  const { data: taskChannels } = useTaskChannels({ dealId });
  const { data: checklist } = useCompanyPackageWithTabsInformation({
    dealId,
    packageId,
  });

  const updatedFieldCount = useMemo(
    () =>
      checklist?.tabs.reduce(
        (partialSum, tab) => partialSum + tab.updated_field_count,
        0
      ) || 0,
    [checklist]
  );

  const [taskCount, unreadMessageCount] = useMemo(() => {
    if (_.isNil(taskChannels)) {
      return [<Skeleton width={10} />, <Skeleton width={10} />];
    }

    const allTasksForPackage = taskChannels.filter(
      (taskChannel) => taskChannel.task.company_package?.id === packageId
    );

    const openTasksForPackage = allTasksForPackage.filter(
      (taskChannel) => !taskChannel.task.completed
    );

    return [
      openTasksForPackage.length,
      _.sumBy(allTasksForPackage, "unread_count"),
    ];
  }, [packageId, taskChannels]);

  return {
    updatedFieldCount,
    taskCount,
    unreadMessageCount,
  };
};
